import { t } from "@lingui/macro";
import { CircleNotch, UploadSimple } from "@phosphor-icons/react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
} from "@reactive-resume/ui";
import { useRef, useState } from "react";

import { useToast } from "@/client/hooks/use-toast";
import { useUploadResume } from "@/client/services/resume/upload";
import { useDialog } from "@/client/stores/dialog";

type UploadStage = "idle" | "uploading" | "processing";

const ACCEPTED_FORMATS = ".pdf,.docx,.txt";

/** Default limit; keep aligned with server `DEFAULT_MAX_FILE_SIZE_BYTES` / `RESUME_UPLOAD_MAX_FILE_SIZE`. */
const MAX_UPLOAD_SIZE_MB = 5;

export const UploadDialog = () => {
  const { toast } = useToast();
  const { isOpen, close } = useDialog("upload");
  const { uploadResume, loading } = useUploadResume();

  const inputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [stage, setStage] = useState<UploadStage>("idle");
  const [uploadProgress, setUploadProgress] = useState(0);

  const onReset = () => {
    setFile(null);
    setStage("idle");
    setUploadProgress(0);
    if (inputRef.current) inputRef.current.value = "";
  };

  const onClose = () => {
    if (!loading) {
      onReset();
      close();
    }
  };

  const onUpload = async () => {
    if (!file) return;

    try {
      setStage("uploading");

      await uploadResume({
        file,
        onProgress: (percent) => {
          setUploadProgress(percent);
          if (percent >= 100) setStage("processing");
        },
      });

      toast({
        variant: "success",
        title: t`Resume uploaded and parsed successfully.`,
      });

      onReset();
      close();
    } catch {
      setStage("idle");
      setUploadProgress(0);

      toast({
        variant: "error",
        title: t`Oops, the server returned an error.`,
        description: t`There was an issue processing your resume. Please check the file and try again.`,
      });
    }
  };

  const isProcessing = stage === "processing";
  const isUploading = stage === "uploading";

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent>
        <div className="space-y-4">
          <DialogHeader>
            <DialogTitle>
              <div className="flex items-center space-x-2.5">
                <UploadSimple />
                <h2>{t`Upload a resume`}</h2>
              </div>
            </DialogTitle>
            <DialogDescription>
              {t`Upload a PDF, DOCX, or TXT file to automatically parse and create a resume in your account. Max size: ${MAX_UPLOAD_SIZE_MB} MB.`}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2">
            <Label>{t`File`}</Label>
            <Input
              ref={inputRef}
              type="file"
              accept={ACCEPTED_FORMATS}
              disabled={loading}
              onChange={(event) => {
                const selected = event.target.files?.[0];
                if (selected) setFile(selected);
              }}
            />
            <p className="text-xs text-secondary-foreground">
              {/* eslint-disable-next-line lingui/no-unlocalized-strings */}
              {t`Accepts .pdf, .docx, and .txt files`}
            </p>
          </div>

          {(isUploading || isProcessing) && (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span>
                  {isProcessing ? t`Parsing your resume...` : t`Uploading...`}
                </span>
                {isUploading && <span>{uploadProgress}%</span>}
              </div>
              <div className="h-2 overflow-hidden rounded-full bg-secondary">
                <div
                  className={`h-full rounded-full transition-all duration-300 ${
                    isProcessing ? "animate-pulse bg-primary/70" : "bg-primary"
                  }`}
                  style={{ width: isProcessing ? "100%" : `${uploadProgress}%` }}
                />
              </div>
              {isProcessing && (
                <div className="flex items-center gap-2 text-xs text-secondary-foreground">
                  <CircleNotch size={14} className="animate-spin" />
                  {t`This may take a few seconds...`}
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="secondary" disabled={loading} onClick={onClose}>
              {t`Cancel`}
            </Button>
            <Button type="button" disabled={!file || loading} onClick={onUpload}>
              {loading ? t`Processing...` : t`Upload & Parse`}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
};
